class ShopCollectionGrid extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    if (!this.sectionId) return;

    this.form = this.querySelector('[js-filter-form]');
    this.filterContainer = this.querySelector('[js-filter-container]');
    this.productsWrapper = this.querySelector('[js-products-wrapper]');
    this.clearAllBtn = this.querySelector('[js-clear-all]');
    this.sortSelect = this.querySelector('[js-sort-select]');
    this.hiddenSortSelect = this.querySelector('[js-sort-by]');
    this.searchInput = this.querySelector('[js-shop-search]');

    this._cache = [];

    this._debouncedSubmit = this._debounce(() => {
      this._onFilterChange();
    }, 500);

    this._debouncedSearch = this._debounce(() => {
      this._applySearch();
    }, 200);

    this._bindListeners();
    this._initAddToCart();
  }

  _debounce(fn, delay) {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), delay);
    };
  }

  _bindListeners() {
    if (this.form) {
      this.form.addEventListener('input', this._debouncedSubmit);
    }

    if (this.clearAllBtn) {
      this.clearAllBtn.addEventListener('click', (e) => this._onClearAll(e));
    }

    if (this.sortSelect) {
      this.sortSelect.addEventListener('change', () => {
        if (this.hiddenSortSelect) {
          this.hiddenSortSelect.value = this.sortSelect.value;
        }
        this._onFilterChange();
      });
    }

    if (this.searchInput) {
      this.searchInput.addEventListener('input', this._debouncedSearch);
    }

    const pagination = this.querySelector('[js-pagination]');
    if (pagination) {
      pagination.addEventListener('click', (e) => {
        const link = e.target.closest('a');
        if (!link) return;
        e.preventDefault();
        const url = new URL(link.href);
        this._fetchAndRender(url.search.replace(/^\?/, ''));
      });
    }

    window.addEventListener('popstate', (e) => this._onPopState(e));
  }

  /* ── Client-side search ── */

  _applySearch() {
    const term = this.searchInput ? this.searchInput.value.toLowerCase().trim() : '';
    const grid = this.querySelector('[js-products-grid]');
    if (!grid) return;

    const cards = grid.querySelectorAll('.shop-product-card');
    let visible = 0;

    cards.forEach((card) => {
      if (!term) {
        card.style.display = '';
        visible++;
        return;
      }
      const title = (card.dataset.title || '').toLowerCase();
      const desc = (card.dataset.description || '').toLowerCase();
      const match = title.includes(term) || desc.includes(term);
      card.style.display = match ? '' : 'none';
      if (match) visible++;
    });

    let empty = this.querySelector('.shop-search-empty');
    if (visible === 0 && term) {
      if (!empty) {
        empty = document.createElement('div');
        empty.className = 'shop-search-empty';
        empty.innerHTML = '<p>No products match your search.</p>';
        grid.parentNode.appendChild(empty);
      }
      empty.style.display = '';
    } else if (empty) {
      empty.style.display = 'none';
    }
  }

  /* ── Server-side collection.filters via AJAX ── */

  _onFilterChange() {
    if (!this.form) return;

    const formData = new FormData(this.form);
    const searchParams = new URLSearchParams();

    for (const [key, value] of formData.entries()) {
      if (value !== null && value !== '') {
        searchParams.append(key, value);
      }
    }

    this._fetchAndRender(searchParams.toString());
  }

  _onClearAll(evt) {
    evt.preventDefault();
    const params = new URLSearchParams(window.location.search);
    const newParams = new URLSearchParams();

    const sortBy = params.get('sort_by');
    if (sortBy) newParams.set('sort_by', sortBy);

    this._fetchAndRender(newParams.toString());
  }

  _onPopState(evt) {
    const searchParams = evt.state?.searchParams ?? '';
    this._fetchAndRender(searchParams, false);
  }

  async _fetchAndRender(searchParams, updateHistory = true) {
    this.classList.add('is-loading');

    const url = `${window.location.pathname}?${searchParams}`;
    const cacheHit = this._cache.find((c) => c.url === url);

    try {
      const parsed = cacheHit
        ? new DOMParser().parseFromString(cacheHit.html, 'text/html')
        : await this._fetchPage(url);

      const newSection = parsed.querySelector(
        `shop-collection-grid[data-section-id="${this.sectionId}"]`
      );
      if (!newSection) return;

      const newFilters = newSection.querySelector('[js-filter-container]');
      if (this.filterContainer && newFilters) {
        this.filterContainer.innerHTML = newFilters.innerHTML;
      }

      const newClearBtn = newSection.querySelector('[js-clear-all]');
      if (this.clearAllBtn && newClearBtn) {
        this.clearAllBtn.className = newClearBtn.className;
      }

      const newWrapper = newSection.querySelector('[js-products-wrapper]');
      if (this.productsWrapper && newWrapper) {
        this.productsWrapper.innerHTML = newWrapper.innerHTML;
      }

      this._rebindAfterRender();

      if (this.searchInput && this.searchInput.value.trim()) {
        this._applySearch();
      }

      if (updateHistory) {
        const fullUrl = searchParams
          ? `${window.location.pathname}?${searchParams}`
          : window.location.pathname;
        history.pushState({ searchParams }, '', fullUrl);
      }
    } catch (err) {
      console.error('[ShopCollectionGrid] filter error:', err);
    } finally {
      this.classList.remove('is-loading');
    }
  }

  async _fetchPage(url) {
    const res = await fetch(url);
    const html = await res.text();
    this._cache.push({ url, html });
    if (this._cache.length > 10) this._cache.shift();
    return new DOMParser().parseFromString(html, 'text/html');
  }

  _rebindAfterRender() {
    this.productsWrapper = this.querySelector('[js-products-wrapper]');

    this.sortSelect = this.querySelector('[js-sort-select]');
    if (this.sortSelect) {
      this.sortSelect.addEventListener('change', () => {
        if (this.hiddenSortSelect) {
          this.hiddenSortSelect.value = this.sortSelect.value;
        }
        this._onFilterChange();
      });
    }

    const pagination = this.querySelector('[js-pagination]');
    if (pagination) {
      pagination.addEventListener('click', (e) => {
        const link = e.target.closest('a');
        if (!link) return;
        e.preventDefault();
        const url = new URL(link.href);
        this._fetchAndRender(url.search.replace(/^\?/, ''));
      });
    }

    this._initAddToCart();
  }

  _initAddToCart() {
    const atcForms = this.querySelectorAll('[data-shop-atc-form]');
    atcForms.forEach((form) => {
      if (form._shopBound) return;
      form._shopBound = true;

      form.addEventListener('submit', (e) => {
        e.preventDefault();
        const btn = form.querySelector('.shop-atc-btn');
        const originalText = btn.textContent;
        btn.disabled = true;
        btn.textContent = 'Adding...';

        const formData = new FormData(form);

        const cartItemsComponents = document.querySelectorAll('cart-items-component');
        const sectionIds = [];
        cartItemsComponents.forEach((item) => {
          if (item.dataset?.sectionId) sectionIds.push(item.dataset.sectionId);
        });
        if (sectionIds.length > 0) formData.append('sections', sectionIds.join(','));

        fetch('/cart/add.js', { method: 'POST', body: formData })
          .then((res) => res.json())
          .then((response) => {
            btn.textContent = 'Added!';
            setTimeout(() => {
              btn.textContent = originalText;
              btn.disabled = false;
            }, 1500);

            if (response.items) {
              fetch('/cart.js')
                .then((r) => r.json())
                .then((cart) => {
                  document.querySelectorAll('[data-cart-count]').forEach((el) => {
                    el.textContent = cart.item_count;
                  });
                });
            }

            document.dispatchEvent(
              new CustomEvent('cart:update', {
                bubbles: true,
                detail: {
                  resource: {},
                  sourceId: '',
                  data: { source: 'shop-collection-grid', sections: response.sections || {} },
                },
              })
            );
          })
          .catch(() => {
            btn.textContent = 'Error';
            setTimeout(() => {
              btn.textContent = originalText;
              btn.disabled = false;
            }, 1500);
          });
      });
    });
  }
}

if (!customElements.get('shop-collection-grid')) {
  customElements.define('shop-collection-grid', ShopCollectionGrid);
}
